import { productTypeDetailsFragment } from "@saleor/fragments/productTypes";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import { TypedMutation } from "../mutations";
import {
  AssignAttribute,
  AssignAttributeVariables
} from "./types/AssignAttribute";
import {
  ProductTypeAttributeFilterableUpdate,
  ProductTypeAttributeFilterableUpdateVariables
} from "./types/ProductTypeAttributeFilterableUpdate";
import {
  ProductTypeAttributeReorder,
  ProductTypeAttributeReorderVariables
} from "./types/ProductTypeAttributeReorder";
import {
  ProductTypeBulkDelete,
  ProductTypeBulkDeleteVariables
} from "./types/ProductTypeBulkDelete";
import {
  ProductTypeCreate,
  ProductTypeCreateVariables
} from "./types/ProductTypeCreate";
import {
  ProductTypeDelete,
  ProductTypeDeleteVariables
} from "./types/ProductTypeDelete";
import {
  ProductTypeUpdate,
  ProductTypeUpdateVariables
} from "./types/ProductTypeUpdate";
import {
  UnassignAttribute,
  UnassignAttributeVariables
} from "./types/UnassignAttribute";

export const productTypeDeleteMutation = gql`
  mutation ProductTypeDelete($id: ID!) {
    productTypeDelete(id: $id) {
      errors {
        field
        message
      }
      productType {
        id
      }
    }
  }
`;
export const TypedProductTypeDeleteMutation = TypedMutation<
  ProductTypeDelete,
  ProductTypeDeleteVariables
>(productTypeDeleteMutation);

export const productTypeBulkDeleteMutation = gql`
  mutation ProductTypeBulkDelete($ids: [ID]!) {
    productTypeBulkDelete(ids: $ids) {
      errors {
        field
        message
      }
    }
  }
`;
export const TypedProductTypeBulkDeleteMutation = TypedMutation<
  ProductTypeBulkDelete,
  ProductTypeBulkDeleteVariables
>(productTypeBulkDeleteMutation);

export const productTypeUpdateMutation = gql`
  ${productTypeDetailsFragment}
  mutation ProductTypeUpdate($id: ID!, $input: ProductTypeInput!) {
    productTypeUpdate(id: $id, input: $input) {
      errors {
        field
        message
      }
      productType {
        ...ProductTypeDetailsFragment
      }
    }
  }
`;
export const useProductTypeUpdateMutation = makeMutation<
  ProductTypeUpdate,
  ProductTypeUpdateVariables
>(productTypeUpdateMutation);

export const assignAttributeMutation = gql`
  ${productTypeDetailsFragment}
  mutation AssignAttribute($id: ID!, $operations: [AttributeAssignInput!]!) {
    attributeAssign(productTypeId: $id, operations: $operations) {
      errors {
        field
        message
      }
      productType {
        ...ProductTypeDetailsFragment
      }
    }
  }
`;
export const TypedAssignAttributeMutation = TypedMutation<
  AssignAttribute,
  AssignAttributeVariables
>(assignAttributeMutation);

export const unassignAttributeMutation = gql`
  ${productTypeDetailsFragment}
  mutation UnassignAttribute($id: ID!, $ids: [ID]!) {
    attributeUnassign(productTypeId: $id, attributeIds: $ids) {
      errors {
        field
        message
      }
      productType {
        ...ProductTypeDetailsFragment
      }
    }
  }
`;
export const TypedUnassignAttributeMutation = TypedMutation<
  UnassignAttribute,
  UnassignAttributeVariables
>(unassignAttributeMutation);

export const productTypeCreateMutation = gql`
  ${productTypeDetailsFragment}
  mutation ProductTypeCreate($input: ProductTypeInput!) {
    productTypeCreate(input: $input) {
      errors {
        field
        message
      }
      productType {
        ...ProductTypeDetailsFragment
      }
    }
  }
`;
export const TypedProductTypeCreateMutation = TypedMutation<
  ProductTypeCreate,
  ProductTypeCreateVariables
>(productTypeCreateMutation);

const productTypeAttributeReorder = gql`
  ${productTypeDetailsFragment}
  mutation ProductTypeAttributeReorder(
    $move: ReorderInput!
    $productTypeId: ID!
    $type: AttributeTypeEnum!
  ) {
    productTypeReorderAttributes(
      moves: [$move]
      productTypeId: $productTypeId
      type: $type
    ) {
      errors {
        field
        message
      }
      productType {
        ...ProductTypeDetailsFragment
      }
    }
  }
`;
export const ProductTypeAttributeReorderMutation = TypedMutation<
  ProductTypeAttributeReorder,
  ProductTypeAttributeReorderVariables
>(productTypeAttributeReorder);

const productTypeAttributeFilterableUpdate = gql`
  ${productTypeDetailsFragment}
  mutation ProductTypeAttributeFilterableUpdate(
    $id: ID!
    $attributeId: ID!
    $attributeType: AttributeTypeEnum!
    $filterbale: Boolean!
  ) {
    productTypeAttributeFilterableUpdate(
      id: $id
      attributeId: $attributeId
      attributeType: $attributeType
      filterbale: $filterbale
    ) {
      errors {
        field
        message
      }
      productType {
        ...ProductTypeDetailsFragment
      }
    }
  }
`;
export const useProductTypeAttributeFilterableUpdateMutation = makeMutation<
  ProductTypeAttributeFilterableUpdate,
  ProductTypeAttributeFilterableUpdateVariables
>(productTypeAttributeFilterableUpdate);
